import React, { useContext, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Divider, Typography, Box, TextField } from '@material-ui/core';
import ErrorHandler from 'utils/ErrorHandler';
import TextRange from 'utils/TextRange';
import { BackIcon, SyncIcon } from 'components/Icons';
import Context from 'components/DuvidasFrequentes/Context';
import api from 'api/Associado/duvidas';
import useStyle from './style';

interface Props {
  usuario: string;
  idEvento: number;
}

const PERGUNTA_MAXLENGHT = 500;

export default function ({ usuario, idEvento }: Props) {
  const { t } = useTranslation();
  const classes = useStyle();
  const { list, atualizarPergunta } = useContext(Context);
  const [duvida, setDuvida] = useState<string>('');
  const [textoValido, setValidacao] = useState<boolean>(true);
  const [isSending, setSending] = useState<boolean>(false);

  const canSend = useMemo(() => duvida.trim().length > 0, [duvida]);

  const limitText = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDuvida(TextRange(event.currentTarget.value, PERGUNTA_MAXLENGHT));
  };

  const validarTexto = useCallback(() => {
    setValidacao(duvida.trim().length > 0);
  }, [duvida]);

  const enviar = useCallback(async () => {
    if (isSending) return;
    setSending(true);
    const { fail, data } = await api.perguntar({
      idAssembleia: idEvento,
      pergunta: duvida,
    });
    setSending(false);
    if (fail) {
      ErrorHandler(fail);
    }
    if (data) {
      toast.success(t('AssembleiaAssociadoRoute.faq.form.success'));
      atualizarPergunta(data);
      list();
    }
  }, [idEvento, duvida, list, t, isSending, atualizarPergunta]);

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbar}>
        <Button variant="text" startIcon={<BackIcon />} className={classes.back} onClick={list}>
          <Typography variant="body1">{t('back')}</Typography>
        </Button>
      </Box>
      <Divider />
      <Box className={classes.content}>
        <TextField
          label={t('AssembleiaAssociadoRoute.faq.form.description.label')}
          className={classes.pergunta}
          helperText={
            !textoValido ? (
              t('LoginAssociado.required-field')
            ) : (
              <React.Fragment>
                <span>{t('AssembleiaAssociadoRoute.faq.form.description.helper')}</span>
                <span>
                  {duvida.length}/{PERGUNTA_MAXLENGHT}
                </span>
              </React.Fragment>
            )
          }
          value={duvida}
          onChange={limitText}
          onBlur={validarTexto}
          inputProps={{
            maxlenght: PERGUNTA_MAXLENGHT,
            inputMode: 'text',
          }}
          error={!textoValido}
          rowsMax={10}
          disabled={isSending}
          multiline
          required
        />
      </Box>
      <footer className={classes.footer}>
        <Button
          variant="contained"
          color="primary"
          className={classes.submit}
          onClick={enviar}
          disabled={!canSend || isSending}
          endIcon={isSending ? <SyncIcon animated /> : undefined}
        >
          {t('send')}
        </Button>
      </footer>
    </Box>
  );
}
